import csiAction from "csi/action";
import comm from "csi/comm";
import def from "csi/def";
import Tab from "csi/def/Tab";
import locale from "csi/locale";
import monitor from "csi/monitor/_base";
import SearchManager from "csi/page/SearchManager";
import ReactView from "csi/react/View";
import reflection from "csi/reflection";
import reflectionHints from "csi/reflectionHints";
import { pushUrlDataToGtm } from "csi/service/googleTagManagerService";
import { populateLayoutStore } from "csi/service/layoutService";
import { getLayoutView } from "csi/service/viewService";
import session from "csi/session";
import { rootCache } from "csi/state/Cache";
import { DataSetupItem, DataSetupSection, dataSetupStore } from "csi/state/datasetup";
import LayoutStore from "csi/state/layout";
import rootStore, { browserStore, localStore, rightPaneStore, routerStore, sessionStore } from "csi/state/rootStore";
import TabsView from "csi/tabs/View";
import { C8Node, C8NodeType, cnl } from "csi/typings";
import { clearPinyinCache } from "csi/widget/LanguageUtil";
import xml from "csi/xml";
import ConfirmDialog from "designSystem/components/dialog/ConfirmDialog";
import MessageDialog from "designSystem/components/dialog/MessageDialog";
import { action, runInAction } from "mobx";
import { clearAggregateStoreCaches } from "src/c8-platform/service/aggregateStoreService";
import JsonResponse from "./comm/JsonResponse";
import { getBannerComponent } from "./registry/bannerRegistry";
import { createPageStore } from "./registry/pageStoreRegistry";
import { getAncestorNodes } from "./service/nodeService";
import { onNewLocation } from "./service/rightPaneService";
import sessionConstants from "./sessionConstants";
import { VIEW_TYPE_REACT, VIEW_TYPE_TABS } from "./state/viewUtils";
import WorkingCache from "./state/WorkingCache";
import { checkNavigation, getHomeTabByType, getLocationHash, LOCATION_REVISION_URL, LOCATION_SECTION, LOCATION_SECTION_ITEM, LOCATION_TAB, LOCATION_TYPE, LOCATION_URL, registerNavigationCheck, setLocationHash } from "./utility/routerUtility";
import { setSessionVariables } from "./middleware/rollup/ClientRollupScope";

const ADMIN_NODES = {
	[reflection.TYPE_BUSINESS_OBJECT]: 1,
	[reflection.TYPE_SITE]: 1,
	CamundaProcessDefinition: 1,
	ConfigurableAttributeGroup: 1,
	DGaaSTemplate: 1,
	ProofTemplate: 1,
	StylePushTemplate: 1,
	Workflow: 1,
};

const FLEX_NODES = {
	[xml.TYPE_USER]: 1,
	Wbs: 1,
};

const FLEX_HEIGHT_NODES = {
	[reflection.TYPE_TASKS]: true,
	ProductPresentation: true,
};

const DATA_SETUP_EXCEPTION_TYPES = new Set([
	// in the case of proofing, one of its ancestor node is PrintDesign, that would bring us to that data setup instance, instead of that,
	// we need to treat it as a non data set up instance and pop up a normal React view
	"ProofWorkflowInstance",
]);

let locationHandles: dojo.Handle[] = [];

/**
 * Trigger page refresh
 */
const refreshLocation = action(() => {
	routerStore.updateBreadcrumb();
	routerStore.updateHostView();
});

/**
 * Make current location dormant and re-enable all views to take effect
 */
const setLocationReadOnly = action(() => {
	const { layoutStore } = rootStore;
	const { locationNode, locationRevisionNode } = routerStore;

	// stop any suspended actions waiting for input
	csiAction.stopAllActions();

	// force all views to read-only so no edits/actions can be performed
	layoutStore.setReadOnly(true, locationNode, locationRevisionNode);
});

/**
 * When location node changes, cache changes and update breadcrumb; if the location node
 * has new revisions, the revision drop-down must be updated to reflect that
 */
const onChangeLocationNode = action((node: C8Node) => {
	rootCache.add(node);
	routerStore.updateBreadcrumb();
});

/**
 * If location revision node becomes frozen, then stop any existing actions and
 * open confirmation dialog asking to refresh
 */
const onChangeLocationRevisionNode = action((revisionNode: C8Node) => {
	const { locationRevisionNode, locationTypeName } = routerStore;

	rootCache.add(revisionNode);

	// if revision has been frozen, confirm with user whether to refresh view or keep working
	const isFrozen = xml.nodeIsFrozen(revisionNode);
	const wasFrozen = xml.nodeIsFrozen(locationRevisionNode);

	if (isFrozen && !wasFrozen) {
		// current revision is revised, approved, frozen, etc.
		const resId = (revisionNode.State === reflection.REVISION_APPROVED) ? "StateApproved" : "StateFrozen";

		routerStore.openDialog(ConfirmDialog, {
			category: "warn",
			confirmRes: "BUTTON_REFRESH",
			messageRes: "LocationStateFrozenMessage",
			messageResParams: [locationTypeName, locale.lowercaseResource(resId)],
			secondaryButtonRes: "BUTTON_CLOSE",
			titleRes: "LocationStateChangeTitle",
			titleResParams: [locationTypeName],

			onCancel: setLocationReadOnly,
			onConfirm: refreshLocation,
		});
	} else if (wasFrozen && !isFrozen) {
		// current revision in pending state and reopened (unfrozen)
		routerStore.openDialog(MessageDialog, {
			buttonRes: "BUTTON_REFRESH",
			category: "info",
			messageRes: "LocationStateUnfrozenMessage",
			messageResParams: [locationTypeName],
			titleRes: "LocationStateChangeTitle",
			titleResParams: [locationTypeName],

			onClose: refreshLocation,
		});
	} else {
		// any other change, just make sure breadcrumb is updated
		routerStore.updateBreadcrumb();
	}
});

/**
 * If location node is deleted, then notify user and navigate to next closest location
 */
const onDeleteLocationNode = action((url: cnl) => {
	const ancestorUrl = getLocationAncestorUrl();
	const { homeUrl } = sessionStore;
	const { locationType, locationTypeName } = routerStore;

	rootCache.deleteNode(url);

	routerStore.openDialog(MessageDialog, {
		buttonRes: "BUTTON_CLOSE",
		category: "warn",
		messageRes: "LocationDeletedMessage",
		messageResParams: [locationTypeName],
		titleRes: "LocationDeletedTitle",
		titleResParams: [locationTypeName],

		// register location change as navigating to a new node
		onClose: () => {
			const hash = getLocationHash({
				[LOCATION_TAB]: (ancestorUrl === homeUrl) ? getHomeTabByType(locationType) : undefined,
				[LOCATION_URL]: ancestorUrl,
			});

			setLocationHash(hash);
		},
	});
});

/**
 * If location node is deleted, then notify user and navigate to next closest location
 */
const onDeleteLocationRevisionNode = action((url: cnl) => {
	const { locationTypeName, locationUrl } = routerStore;

	rootCache.deleteNode(url);

	routerStore.openDialog(MessageDialog, {
		buttonRes: "BUTTON_CLOSE",
		category: "warn",
		messageRes: "LocationRevisionDeletedMessage",
		messageResParams: [locationTypeName],
		titleRes: "LocationRevisionDeletedTitle",
		titleResParams: [locationTypeName],

		// changing revisions should not add to browser history
		onClose: () => setLocationUrl(locationUrl),
	});
});

/**
 * Test if the current node should be in admin context
 */
export function isAdminMode(locationNode: C8Node, previousMode: boolean): boolean {
	const locationType = locationNode?.$Type || "";

	if (locationType == reflection.TYPE_DATA_SETUP) {
		return false;
	} else if (!FLEX_NODES[locationType]) {
		const rc = reflection.getNodeClass(locationNode);
		const supportSubtype = rc.isDerivedFrom("Subtype");
		const isAdminNode = ADMIN_NODES[locationType];

		return (supportSubtype || !!isAdminNode);
	}

	return previousMode;
}

/**
 * Return true if currently observing the location nodes; if not, then that would generally
 * mean that user is in the middle of navigating to a new node / revision
 */
export function isObservingLocation(): boolean {
	return !!locationHandles.length;
}

/**
 * Return true if system can render content for given node
 */
export function canNavigateToNode(node: C8Node): boolean {
	const type = node.$Type;

	// only allow navigating to data setup if there are available sections
	if (type === reflection.TYPE_DATA_SETUP) {
		return !!dataSetupStore.availableSections.length;
	}

	return def.canNavigateToType(type);
}

/**
 * Open confirmation dialog to confirm whether or not any pending changes should be discarded
 */
export async function confirmLeavePage(): Promise<void> {
	return new Promise((resolve, reject) => {
		routerStore.openDialog(ConfirmDialog, {
			category: "error",
			confirmRes: "BUTTON_LEAVE_PAGE",
			messageRes: "LeavePageMessage",
			titleRes: "LeavePageTitle",
			onCancel: reject,
			onConfirm: resolve,
		});
	});
}

/**
 * Return url of node that serves as immediate ancestor of current location node
 */
export function getLocationAncestorUrl(): cnl {
	const { lastAdminMode } = localStore;
	const { homeUrl, siteUrl } = sessionStore;
	const { locationUrl, renderDataSetupInstance } = routerStore;

	const ancestors = getAncestorNodes(locationUrl, rootCache);
	const ancestor = ancestors.find((node) => def.canNavigateToType(node.$Type) ? node : null);
	if (ancestor) {
		return ancestor.$URL;
	}

	if (renderDataSetupInstance) {
		return reflection.URL_DATA_SETUP;
	}

	return lastAdminMode ? siteUrl : homeUrl;
}

/**
 * Return url of node that serves as context to current location node
 */
export function getLocationContextUrl(node: C8Node): cnl | null {
	if (!SearchManager.getSearchType(node.$Type)) {
		while (node = rootCache.get(node.__Parent__)) {
			if (SearchManager.getSearchType(node.$Type)) {
				return node.$URL;
			}
		}
	}

	return null;
}

/**
 * Recreate UI for current location
 */
export async function refreshCurrentLocation(): Promise<void> {
	return setLocationUrl(routerStore.locationUrl);
}

/**
 * Reserve location keys used by router
 */
export function reserveLocationKeys(): void {
	session.reserveLocationKey(LOCATION_URL, setLocationUrl);
	session.reserveLocationKey(LOCATION_TYPE, null);
	session.reserveLocationKey(LOCATION_REVISION_URL, setLocationRevisionUrl);
}

/**
 * Query the server for new versions of location nodes
 */
export async function resolveCurrentLocation(): Promise<void> {
	const { locationUrl, locationRevisionUrl } = routerStore;
	return resolveLocation(locationUrl, locationRevisionUrl, false);
}

/**
 * Resolve the data setup section and item if navigating there
 */
function resolveDataSetup(nodeType: C8NodeType): boolean {
	if (nodeType !== reflection.TYPE_DATA_SETUP) {
		return false;
	}

	const { availableSections } = dataSetupStore;
	const { targetSection, targetSectionItem } = routerStore;
	let sectionId = targetSection;
	let itemId = targetSectionItem;
	let section: DataSetupSection;

	// find section with id or item
	if (sectionId || itemId) {
		section = availableSections.find((section) => {
			return sectionId ? section.id === sectionId : !!section.findItem(itemId);
		});
	}

	// there must be at least one available section to get here, as checked in canNavigateToType
	section = section || availableSections[0];
	console.assert(!!section);
	sectionId = section.id;

	// determine item to navigate to
	const localDataSetup = localStore.dataSetup;
	const { lastSectionItems } = localDataSetup;
	const { availableItems } = section;
	const lastItemId = lastSectionItems[sectionId];

	const item = availableItems.find((item) => item.id === itemId) ||
		availableItems.find((item) => item.id === lastItemId) ||
		availableItems[0];

	itemId = item.id;

	if (item !== dataSetupStore.activeItem) {
		// if group contains item, then expand it; only necessary if group has an id, which means it
		// has a name and is displayed
		const group = section.findGroupWithItem(itemId);
		if (group?.id) {
			localDataSetup.expandGroup(group.id);
		}
	}

	// update state with section and item
	dataSetupStore.setSectionAndItem(section, item);

	// track last item navigated to within a section
	runInAction(() => {
		lastSectionItems[sectionId] = itemId;
	});

	return true;
}

/**
 * Determine if navigating to data setup item instance, and if so, resolve the section and item
 */
function resolveDataSetupInstance(node: C8Node): boolean {
	if (DATA_SETUP_EXCEPTION_TYPES.has(node.$Type)) {
		return false;
	}

	// if node, or any of its ancestors, are in data setup, then set appropriate section and item
	const nodes = getAncestorNodes(node.$URL, rootCache, true);
	let item: DataSetupItem;
	let section: DataSetupSection;

	const itemNode = nodes.find((node) => {
		const subtypeName = reflection.getSubtypeName(node.Subtype);
		[section, item] = dataSetupStore.getSectionAndItemForType(node.$Type, subtypeName);
		return !!item;
	});

	if (!item?.available) {
		return false;
	}

	// data setup manages templates that are the same type as nodes that are not templates;
	// in such cases, check if the node really is a template
	if (reflectionHints.isTemplateType(itemNode.$Type) && !reflectionHints.isTemplateNode(itemNode)) {
		return false;
	}

	dataSetupStore.setSectionAndItem(section, item);
	return true;
}

/**
 * Query for given node and revision to verify they exist and are valid locations for current user
 * @param url url of node to navigate to
 * @param revisionUrl url of revision to view if node is revisable (defaults to current revision)
 * @param recreateViews set to true if recreating views rather than just updating
 */
export async function resolveLocation(url: cnl, revisionUrl: cnl, recreateViews: boolean): Promise<void> {
	const { setupViewer } = sessionConstants;
	const { locationType } = routerStore;

	if (!reflection.isInitialized()) {
		// if no reflection cache, then go to Setup page unless not allowed
		if (!setupViewer) {
			throw new Error(locale.resource("SERVER_UNRESPONSIVE"));
		}

		url = reflection.URL_SITE;
		revisionUrl = null;
	} else if (url && (url == reflection.URL_SITE) && !setupViewer) {
		// ensure that we don't navigate to Setup when not allowed
		url = null;
	}

	if (!xml.isValidURL(url)) {
		// go to default location, either Home or Setup
		url = session.getLocationDefault(url);
		revisionUrl = null;

		if (!url) {
			throw new Error(locale.resource("SERVER_UNRESPONSIVE"));
		}
	}

	// request nodes from server
	const merchSeasonUrl = localStore.merch.seasonUrl;
	const request = comm.getBreadcrumbRequest(url, revisionUrl, merchSeasonUrl);
	let breadcrumbUrls: cnl[];
	let linkedNodeUrl: cnl;
	let hasWorkflow = false;
	let node: C8Node;
	let response1: JsonResponse;
	let response2: JsonResponse;

	try {
		response1 = await request.go();
		breadcrumbUrls = response1.getResultUrls().reverse();
		linkedNodeUrl = response1.getAttributeValueById("LinkedNode");
		node = response1.getResultNodes()[0];
		hasWorkflow = !!response1.getAttributeValueById("hasValidWorkflow");
	} catch (err) {
		// signal error if not on page load
		if (locationType) {
			throw new Error(locale.resource("Location_ErrorNode"));
		} else {
			node = null;
		}
	}

	// since response is not yet cached, check it and cache for nodes
	const getNode = (url: cnl): C8Node | undefined => response1?.getNode(url) ?? rootCache.get(url);

	// validate master and revision nodes
	const revisionNode = validateRevisionNode(node, getNode(revisionUrl));
	if (revisionNode) {
		node = getNode(revisionNode.__Parent__);
	}

	// ensure that we can navigate to node
	if (node && !canNavigateToNode(node)) {
		// signal error if not on page load
		if (locationType) {
			throw new Error(locale.resource("Location_ErrorType"));
		} else {
			node = null;
		}
	}

	if (!node) {
		// if node does not exist, try going to default location
		url = session.getLocationDefault(url);

		if (!url) {
			// signal error if already tried default location
			throw new Error(locale.resource("SERVER_UNRESPONSIVE"));
		}

		const request = comm.getNodeRequest(url);
		response2 = await request.go();
		node = response2.getResultNode();
		breadcrumbUrls = node ? [node.$URL] : [];
	}

	runInAction(() => {
		unsubscribeToLocation();
		rootCache.cacheResponse(response1);
		rootCache.cacheResponse(response2);
		routerStore.setBreadcrumbUrls(breadcrumbUrls);
		routerStore.setLinkedNodeUrl(linkedNodeUrl);
		subscribeToLocation(node, revisionNode);
	});

	await setLocationNodes(node, revisionNode, hasWorkflow, recreateViews);

	pushUrlDataToGtm();
}

/**
 * Switch to new revision and update browser location without adding new browser history entry
 * @param node node to navigate to
 * @param revisionNode revision of node to view if node is revisable (defaults to current revision)
 * @param recreateViews set to true if recreating views rather than just updating
 */
const setLocationNodes = async (node: C8Node, revisionNode: C8Node, hasWorkflow: boolean, recreateViews: boolean): Promise<void> => {
	const {
		disableTabMatching,
		locationUrl: previousLocationUrl,
		locationRevisionUrl: previousLocationRevisionUrl,
		targetTab,
		renderDataSetup,
		trackDataSetupReturnUrl,
	} = routerStore;

	const url = node.$URL;
	const type = node.$Type;
	const revisionUrl = revisionNode ? revisionNode.$URL : null;
	const currentRevisionUrl = node[reflection.ATTR_CURRENT_REVISION];
	const locationRevisionUrl = (revisionUrl && (revisionUrl === currentRevisionUrl)) ? "" : revisionUrl;
	const historyKey = reflectionHints.getLocationHistoryKey(node, revisionNode);
	const cache = new WorkingCache();

	// check for data setup
	const wasDataSetup = renderDataSetup !== false;
	const isDataSetupInstance = resolveDataSetupInstance(node);
	const isDataSetup = isDataSetupInstance || resolveDataSetup(type);
	const updateRightPane = (url !== previousLocationUrl) || (locationRevisionUrl !== previousLocationRevisionUrl);

	let component: string;
	let componentProps: object;
	let layoutStore: LayoutStore;

	// determine component to use for rendering type
	const layoutView = getLayoutView(node, revisionNode);
	const viewType = layoutView?.getType();

	if (isDataSetup) {
		// data setup uses specific component
		component = "csi/datasetup/DataSetupLayout";
	} else if (viewType === VIEW_TYPE_REACT) {
		const reactView = layoutView as ReactView;
		component = reactView.componentName;
		componentProps = reactView.properties;
	} else {
		component = "csi/page/PageContent";
	}

	// create layout store
	if (layoutView) {
		const useFlexHeight = isDataSetupInstance || FLEX_HEIGHT_NODES[type];
		const tabsView = (viewType === VIEW_TYPE_TABS) ? layoutView as TabsView : null;
		const hostViewId = tabsView?.m_id;
		const headerViewId = tabsView ? tabsView.idHeaderView : layoutView.m_id;
		const locationParams = {
			[LOCATION_URL]: url,
			[LOCATION_REVISION_URL]: locationRevisionUrl,
		};

		if (tabsView) {
			// add workflow approval tab if revision can be approved by workflow - only allowed for host users
			if (hasWorkflow &&
				revisionNode &&
				reflection.getNodeSetting(node, "WorkflowName") &&
				!sessionConstants.userCompanyUrl
			) {
				const tab = new Tab();
				tab.m_idView = "ApprovalWorkflow";
				tabsView.tabs.push(tab);
			}

			// type-specific processing of view
			await reflectionHints.customizeLocationLayout(tabsView, node, revisionNode);
		}

		layoutStore = new LayoutStore(revisionUrl || url, hostViewId, locationParams, historyKey, headerViewId, useFlexHeight, cache);

		// populate cache - this won't be necessary once we move away from old cache, but for now it is
		const { cacheView } = layoutStore.viewParams;
		cacheView.add(node);
		cacheView.add(revisionNode);

		if (tabsView) {
			await populateLayoutStore(layoutStore, tabsView);
		}
	}

	// get page store if configured
	const pageStore = await createPageStore(node, revisionNode, cache);

	runInAction(() => {
		browserStore.exitFullScreen();

		// clear aggregate store caches when navigating to new location
		// NOTE: this may be too conservative because navigating does not really change any data;
		// it does however prevent the cache from growing too large. We could try to remove at some point.
		clearAggregateStoreCaches();

		// clear Pinyin cache when navigating to new location so it does not grow too large
		clearPinyinCache();

		// force breadcrumb and host view to redraw, even if at same location - not all state is in MobX yet
		routerStore.updateBreadcrumb();

		if (recreateViews) {
			routerStore.updateHostView();
		}

		// if navigating to an item instance of data setup, then track previous location where user can return to;
		// this needs to be reset when the active item in data setup changes
		if (isDataSetupInstance && trackDataSetupReturnUrl && !wasDataSetup) {
			browserStore.fullScreenLeftDrawerClosedInitially = true;
			dataSetupStore.returnUrl = routerStore.locationUrl;
			routerStore.trackDataSetupReturnUrl = false;
		}

		routerStore.renderBreadcrumb = !isDataSetup;
		routerStore.renderDataSetup = isDataSetup;
		routerStore.renderDataSetupInstance = isDataSetupInstance;
		routerStore.setFeatureComponent(component, componentProps);

		// by default, reserve space for vertical scrollbar; to disable, reset the state when rendering a particular view (such as in CanvasWidget)
		routerStore.locationContextUrl = getLocationContextUrl(node);
		routerStore.setLocation(node, revisionNode);
		setSessionRollupVariables();

		// update the state which the breadcrumb relies on
		localStore.lastAdminMode = isAdminMode(node, localStore.lastAdminMode);

		session.setLocationActual(LOCATION_URL, url, null, setLocationUrl, !recreateViews);
		session.setLocationActual(LOCATION_TYPE, node.$Type, null, null);
		session.setLocationActual(LOCATION_REVISION_URL, locationRevisionUrl, null, setLocationRevisionUrl, true);

		// update right pane when navigating to a new location
		if (updateRightPane) {
			onNewLocation();
		}

		// manage location keys for data setup
		if (isDataSetup) {
			const { activeItem, activeSection } = dataSetupStore;
			session.setLocationActual(LOCATION_SECTION, activeSection.id, null, setDataSetupLocation);
			session.setLocationActual(LOCATION_SECTION_ITEM, activeItem.id, null, setDataSetupLocation);
		} else if (wasDataSetup) {
			dataSetupStore.setSectionAndItem(null, null);
			session.releaseLocationKey(LOCATION_SECTION);
			session.releaseLocationKey(LOCATION_SECTION_ITEM);
		}

		rootStore.setPageStoreAndContext(pageStore);

		// manage layout (tabs) - do so at the bottom because some tabs/views depend on the location node,
		// so that needs to be set first
		if (layoutStore) {
			if (recreateViews) {
				session.reserveLocationKey(LOCATION_TAB, setLocationTab);
			}

			// find default view to select - either from Tab parameter, local storage, or just first tab
			const targetIds = [targetTab];

			if (!disableTabMatching) {
				targetIds.push(localStore.viewHistory[historyKey]);
			}

			layoutStore.resolveSelection(targetIds);
		}

		rootStore.setLayoutStore(layoutStore);

		// update help pane based on new location
		updateHelpContent();

		// process the banner if configured
		const Banner = getBannerComponent(type);
		routerStore.setBannerComponent(Banner, null);
	});
};

export async function setDataSetupLocation(value: string): Promise<void> {
	await setLocationNodes(routerStore.locationNode, null, false, true);
}

/**
 * Navigate to new node
 */
export async function setLocationUrl(url: cnl, revisionUrl?: cnl): Promise<void> {
	await resolveLocation(url, revisionUrl, true);
}

/**
 * Navigate to new revision URL
 */
export async function setLocationRevisionUrl(revisionUrl: cnl): Promise<void> {
	await resolveLocation(routerStore.locationUrl, revisionUrl, true);
}

/**
 * Navigate to selected tab unless not allowed
 */
export async function setLocationTab(tabId: string): Promise<void> {
	const allowed = await checkNavigation(false, true);
	if (!allowed) {
		return;
	}

	const { layoutStore } = rootStore;
	const { resolveSelection, selectedItem } = layoutStore;
	const { id, locationState, view } = resolveSelection([tabId]);

	// if change tab, exit full screen
	browserStore.exitFullScreen();

	// TODO: in future (when all MobX), we could just update locationState with current values from routerStore.urlParameters,
	// rather than require the view to do it for us
	view?.onTabChanging(locationState);

	// register tab location change
	// TODO - this should be part of reaction to selectedItem changing
	selectedItem.locationState = session.setLocationActual(LOCATION_TAB, id, locationState, setLocationTab);

	pushUrlDataToGtm();
	// update help content
	updateHelpContent();
}

/**
 * Update rollup variables from session variables
 */
export function setSessionRollupVariables(): void {
	const { debugMode } = sessionStore;
	const { locationUrl, locationType } = routerStore;
	const { serverTimeZoneOffset } = sessionConstants;

	setSessionVariables({
		debugMode,
		locationType,
		locationUrl,
		serverTimeZoneOffset,
	});
}

/**
 * Clear any existing location handles and subscribe to location nodes
 */
export function subscribeToLocation(node: C8Node, revisionNode: C8Node): void {
	if (node) {
		locationHandles = [
			monitor.onChangeNode(node.$URL, onChangeLocationNode),
			monitor.onDeleteNode(node.$URL, onDeleteLocationNode),
		];

		if (revisionNode) {
			locationHandles = [
				...locationHandles,
				monitor.onChangeNode(revisionNode.$URL, onChangeLocationRevisionNode),
				monitor.onDeleteNode(revisionNode.$URL, onDeleteLocationRevisionNode),
			];
		}
	}
}

/**
 * Stop subscriptions to location nodes
 */
export function unsubscribeToLocation(): void {
	for (const handle of locationHandles) {
		handle.remove();
	}

	locationHandles = [];
}

/**
 * Update router properties for help content
 */
export function updateHelpContent(): void {
	// TODO: consider doing this in autorun, which is only registered when help pane is open
	const { layoutStore } = rootStore;
	const { renderDataSetup, renderDataSetupInstance, locationType } = routerStore;
	const layoutItem = layoutStore?.selectedItem;
	let title = "";
	let type = "";
	let view = "";

	if (renderDataSetup && !renderDataSetupInstance) {
		// help is for item view in data setup
		const { activeItem } = dataSetupStore;
		if (activeItem) {
			view = activeItem.viewId;
			title = activeItem.name;
			type = activeItem.id;
		}
	} else if (layoutItem) {
		// find active view/type for regular C8 views
		const layoutType = layoutItem.helpType;
		if (layoutType) {
			view = layoutItem.id;
			title = layoutItem.name;
			type = layoutItem.helpType;
		}
	} else {
		// no tabs - just single view
		view = type = locationType;
		title = locale.type(locationType);
	}

	routerStore.setHelpPaneView(title, type, view);
}

/**
 * From the given node and revision node, return the reivison node to navigate to
 */
export function validateRevisionNode(node: C8Node, revisionNode: C8Node): C8Node {
	// if navigated directly to revision, then return node
	if (reflection.isRevisionNode(node)) {
		return node;
	}

	if (reflection.isMasterNode(node)) {
		// if given revision matches master, then return
		if (revisionNode && (node.$URL == revisionNode.__Parent__)) {
			return revisionNode;
		}

		// otherwise, try navigating to latest revision - either when navigating to master, or navigating to revision that does not exist
		return rootCache.get(node[reflection.ATTR_LATEST_REVISION]);
	}

	// if not master or revision node, then revision must be null
	return null;
}

/** check to see if navigation should be allowed based on status of selected right pane */
async function checkRightPaneNavigation(isPageNavigation: boolean, isTabNavigation: boolean): Promise<boolean> {
	const { closeSelected, selected } = rightPaneStore;

	if (selected) {
		const { closeOnTabChange, confirmLeaving } = selected.config;

		if (isPageNavigation || (isTabNavigation && closeOnTabChange)) {
			if (confirmLeaving?.()) {
				try {
					await confirmLeavePage();
					closeSelected();
				} catch (err) {
					return false;
				}
			} else if (closeOnTabChange) {
				closeSelected();
			}
		}
	}

	return true;
}

registerNavigationCheck(checkRightPaneNavigation);